import { useState } from 'react'

import { useOidcIdToken } from '@axa-fr/react-oidc-context'
import { Box, Button, Grid } from '@mui/material'
import { toast } from 'react-toastify'

import { CardTitle, ConfirmDialog } from 'components/common'
import {
  GetNotificationsDocument,
  SortEnumType,
  TaskStatus,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from 'generated/graphql'
import { GET_PAGED_NOTIFICATIONS, GET_TASKS_QUERY } from 'graphql/queries'
import { TaskDialogOptions, useDialog } from 'providers'
import { Restricted } from 'providers/PermissionProvider'

import TaskList from './TaskList'

interface IProps {
  bookingId: string | null
}

const Tasks = (props: IProps) => {
  const { bookingId } = props

  const { dialogOpen } = useDialog<TaskDialogOptions>()
  const { idTokenPayload } = useOidcIdToken()

  const [taskIdToDelete, setTaskIdToDelete] = useState<string | null>(null)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)

  // mutations
  const [updateTask] = useUpdateTaskMutation({
    onCompleted: (response) => {
      toast.success(`Task (${response.updateTask?.id}) was marked as Completed.`)
    },
    update(cache, { data }) {
      if (data?.updateTask) {
        cache.updateQuery(
          {
            query: GET_PAGED_NOTIFICATIONS,
            variables: {
              where: {
                username: { eq: idTokenPayload.name },
                isRead: { eq: false },
              },
              order: {
                createdAt: SortEnumType.Desc,
              },
              first: 10,
            },
          },
          (queryData) => {
            const taskId = Number(data.updateTask?.id)
            const edges = queryData.notifications.edges.filter((e) => e.node.taskId !== taskId)
            const totalCount =
              queryData.notifications.totalCount -
              queryData.notifications.edges.filter((e) => Number(e.node.taskId) === taskId).length

            return {
              notifications: { ...queryData.notifications, edges, totalCount },
            }
          },
        )
      }
    },
  })

  const [deleteTask] = useDeleteTaskMutation({
    onCompleted: () => toast.success('Task was deleted with success.'),
    // update function for cache don't work :(
    refetchQueries: [
      {
        query: GetNotificationsDocument,
        variables: {
          where: {
            username: { eq: idTokenPayload.name },
            isRead: { eq: false },
          },
          order: {
            createdAt: SortEnumType.Desc,
          },
          first: 10,
        },
      },
    ],
  })

  const handleAddTask = () => {
    dialogOpen({ id: null, bookingId })
  }

  const handleTaskClick = (id) => {
    dialogOpen({ id: Number(id) })
  }

  const handleCheckboxChange = (value: boolean, task) => {
    if (value) {
      updateTask({
        variables: {
          input: {
            id: Number(task.id),
            bookingId: task.bookingId,
            description: task.description,
            assignedUserName: task.assignedUserName,
            endDate: task.endDate,
            status: TaskStatus.Completed,
          },
        },
      })
    }
  }

  const handleDeleteClick = (id) => {
    setTaskIdToDelete(id)
    setOpenDeleteConfirmation(true)
  }

  const handleConfirmDelete = () => {
    if (taskIdToDelete === null) {
      return
    }

    deleteTask({
      variables: { id: Number(taskIdToDelete) },
      update(cache) {
        if (bookingId) {
          cache.updateQuery(
            {
              query: GET_TASKS_QUERY,
              variables: { where: { bookingId: { eq: Number(bookingId) } } },
            },
            (queryData) => {
              const edges = queryData.tasks.edges.filter(
                (taskEdge) => taskIdToDelete !== taskEdge.node.id,
              )
              const totalCount = queryData.tasks.totalCount - 1

              return {
                tasks: { ...queryData.tasks, edges, totalCount },
              }
            },
          )
        }

        cache.updateQuery(
          {
            query: GET_TASKS_QUERY,
            variables: { where: { assignedUserName: { eq: idTokenPayload.name } } },
          },
          (queryData) => {
            const edges = queryData.tasks.edges.filter(
              (taskEdge) => taskIdToDelete !== taskEdge.node.id,
            )
            const totalCount = queryData.tasks.totalCount - 1

            return {
              tasks: { ...queryData.tasks, edges, totalCount },
            }
          },
        )
      },
    })
  }

  const handleCancelDelete = () => {
    setTaskIdToDelete(null)
  }

  return (
    <>
      <Box paddingX={2} paddingTop={2}>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <CardTitle>Tasks</CardTitle>
          </Grid>
          <Restricted to='CreateTask'>
            <Grid item>
              <Button variant='outlined' onClick={handleAddTask}>
                Add Task
              </Button>
            </Grid>
          </Restricted>
        </Grid>
      </Box>

      <TaskList
        bookingId={bookingId}
        onTaskClick={handleTaskClick}
        onCheckboxChange={handleCheckboxChange}
        onDeleteClick={handleDeleteClick}
      />

      <ConfirmDialog
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title='Confirmation'
        message='Are you sure you want to delete this task?'
        option1='No'
        option2='Yes'
      />
    </>
  )
}

export default Tasks
